import { useState, useMemo, useCallback } from 'react';

/**
 * CareflowEntityPicker — reusable searchable multi-select using the shared
 * ws-device-picker layout (search + Select All / Clear / Show Selected +
 * scrollable checkbox list). Selection is controlled via selectedIds/onChange
 * (array of string IDs), so the parent can collect the IDs into a single
 * payload — used by the Add Careflow drawer for the main assignment (devices /
 * resident groups / area groups) and for mobile / pager recipient groups.
 *
 * Props:
 *   label            — picker heading (e.g. "Devices", "Groups")
 *   items            — array of option objects
 *   idKey            — object key holding the option's id
 *   nameKey          — object key holding the display name
 *   descKeys         — array of keys whose values are joined for the sub-line
 *   searchKeys       — array of keys to match the search term against
 *   selectedIds      — array of selected ids (as strings)
 *   onChange         — (nextIds: string[]) => void
 *   searchPlaceholder
 *   emptyText        — message when no items match
 */
const CareflowEntityPicker = ({
    label,
    items,
    idKey,
    nameKey,
    descKeys = [],
    searchKeys,
    selectedIds,
    onChange,
    searchPlaceholder = 'Search...',
    emptyText = 'No items found',
}) => {
    const [search, setSearch] = useState('');
    const [showSelectedOnly, setShowSelectedOnly] = useState(false);

    const effectiveSearchKeys = searchKeys && searchKeys.length ? searchKeys : [nameKey, ...descKeys];

    const filtered = useMemo(() => {
        let list = [...(items || [])];
        if (showSelectedOnly) {
            list = list.filter((it) => selectedIds.includes(String(it[idKey])));
        }
        if (search.trim()) {
            const term = search.toLowerCase();
            list = list.filter((it) =>
                effectiveSearchKeys.some((k) => (it[k] || '').toString().toLowerCase().includes(term))
            );
        }
        list.sort((a, b) =>
            (a[nameKey] || '').toString().localeCompare((b[nameKey] || '').toString(), undefined, { sensitivity: 'base' })
        );
        return list;
    }, [items, search, showSelectedOnly, selectedIds, idKey, nameKey, effectiveSearchKeys]);

    const handleToggle = useCallback((id) => {
        const strId = String(id);
        onChange(
            selectedIds.includes(strId)
                ? selectedIds.filter((x) => x !== strId)
                : [...selectedIds, strId]
        );
    }, [selectedIds, onChange]);

    const handleSelectAll = useCallback(() => {
        const allFiltered = filtered.map((it) => String(it[idKey]));
        onChange([...new Set([...selectedIds, ...allFiltered])]);
    }, [filtered, idKey, selectedIds, onChange]);

    const handleClear = useCallback(() => onChange([]), [onChange]);

    return (
        <div className="ws-device-picker">
            <div className="ws-device-picker__header">
                <p className="dp-drawer__label" style={{ marginBottom: 0 }}>
                    {label}
                    <span className="ws-device-picker__count">({selectedIds.length} selected)</span>
                </p>
            </div>

            <div className="ws-device-picker__search">
                <i className="fe fe-search ws-device-picker__search-icon" aria-hidden="true" />
                <input
                    type="text"
                    placeholder={searchPlaceholder}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="ws-device-picker__search-input"
                />
            </div>

            <div className="ws-device-picker__actions">
                <button type="button" onClick={handleSelectAll} className="ws-device-picker__action-btn">Select All</button>
                <button type="button" onClick={handleClear} className="ws-device-picker__action-btn">Clear</button>
                <button
                    type="button"
                    onClick={() => setShowSelectedOnly((prev) => !prev)}
                    className={`ws-device-picker__action-btn${showSelectedOnly ? ' ws-device-picker__action-btn--active' : ''}`}
                >
                    Show Selected
                </button>
            </div>

            <div className="ws-device-picker__list">
                {filtered.length === 0 ? (
                    <p className="ws-device-picker__empty">{showSelectedOnly ? 'No selections.' : emptyText}</p>
                ) : (
                    filtered.map((it) => {
                        const id = String(it[idKey]);
                        const isChecked = selectedIds.includes(id);
                        const desc = descKeys.map((k) => it[k]).filter(Boolean).join(' · ');
                        return (
                            <label key={id} className={`ws-device-picker__item${isChecked ? ' is-selected' : ''}`}>
                                <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => handleToggle(it[idKey])}
                                    className="ap-checkbox"
                                />
                                <div className="ws-device-picker__item-info">
                                    <span className="ws-device-picker__item-name">{it[nameKey] || '—'}</span>
                                    {desc ? <span className="ws-device-picker__item-desc">{desc}</span> : null}
                                </div>
                            </label>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default CareflowEntityPicker;
